import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  GROUP_MEMBERSHIP_ROLES,
  STRUCTURE_CODE_PATTERN,
  type Branch,
  type Campus,
  type CreateBranchRequest,
  type CreateCampusRequest,
  type InstitutionStructure,
} from '@/types/platform';
import styles from './platform-pages.module.css';

/**
 * PA-03 — the physical and organisational skeleton of an institution.
 *
 * WHAT IS DELIBERATELY ABSENT: academic years, programmes, classes and subjects.
 * They belong to EduCore and are not mirrored here. This screen bootstraps the
 * container — institution, campuses, branches, group memberships — so that EduCore
 * has something to attach teaching to, and then stops. A control here that started
 * modelling teaching would be the first step towards two systems disagreeing about
 * what a class is.
 *
 * HEAD OFFICE IS SINGULAR AND THE FORM SAYS SO. At most one branch per institution
 * holds the designation, enforced by a partial unique index rather than by
 * application code, and setting it MOVES it off whichever branch currently holds
 * it. That is a consequence people should read before they tick the box, not
 * discover afterwards when invoices stop arriving where they used to.
 */

export function InstitutionStructurePanel({ institutionId }: { institutionId: string }) {
  const { t } = useTranslation();

  const fetcher = useCallback(() => platformApi.structure.get(institutionId), [institutionId]);
  const { state, reload } = useAsyncData<InstitutionStructure>(
    `structure:${institutionId}`,
    fetcher,
    institutionId.length > 0,
  );

  const [campusOpen, setCampusOpen] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);

  const campuses = state.status === 'ready' ? state.data.campuses : [];

  return (
    <Panel
      title={t('platform.structure.title')}
      icon="institution"
      actions={
        <>
          <button type="button" className={styles.primaryBtnSm} onClick={() => setCampusOpen(true)}>
            <Icon name="plus" size={15} />
            {t('platform.structure.addCampus')}
          </button>
          <button
            type="button"
            className={styles.primaryBtnSm}
            onClick={() => setBranchOpen(true)}
            disabled={campuses.length === 0}
          >
            <Icon name="plus" size={15} />
            {t('platform.structure.addBranch')}
          </button>
        </>
      }
    >
      <p className={styles.dataNotice}>
        <Icon name="info" size={15} aria-hidden="true" />
        {t('platform.structure.scopeNotice')}
      </p>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.campuses.length === 0 && data.branches.length === 0 && data.groups.length === 0}
        emptyTitle={t('platform.structure.emptyTitle')}
        emptyBody={t('platform.structure.emptyBody')}
      >
        {(structure) => <StructureTree structure={structure} />}
      </AsyncPanel>

      <AddCampusDialog
        open={campusOpen}
        institutionId={institutionId}
        onClose={() => setCampusOpen(false)}
        onAdded={() => {
          setCampusOpen(false);
          reload();
        }}
      />

      <AddBranchDialog
        open={branchOpen}
        institutionId={institutionId}
        campuses={campuses}
        existingHeadOffice={
          state.status === 'ready' ? state.data.branches.find((branch) => branch.isHeadOffice) : undefined
        }
        onClose={() => setBranchOpen(false)}
        onAdded={() => {
          setBranchOpen(false);
          reload();
        }}
      />
    </Panel>
  );
}

/**
 * Campuses with their branches nested beneath them.
 *
 * Nested rather than two flat lists: a branch is meaningless without its campus,
 * and "which site does the head office sit on" is one of the two questions this
 * panel exists to answer. A branch whose campus is missing from the response is
 * still shown, under a heading that says so, rather than dropped — a silently
 * omitted branch is how an institution ends up with a site nobody bills.
 */
function StructureTree({ structure }: { structure: InstitutionStructure }) {
  const { t } = useTranslation();

  const byCampus = useMemo(() => {
    const map = new Map<string, Branch[]>();
    for (const branch of structure.branches) {
      const list = map.get(branch.campusId) ?? [];
      list.push(branch);
      map.set(branch.campusId, list);
    }
    return map;
  }, [structure.branches]);

  const orphaned = structure.branches.filter(
    (branch) => !structure.campuses.some((campus) => campus.id === branch.campusId),
  );

  return (
    <>
      <ul className={styles.treeList}>
        {structure.campuses.map((campus) => (
          <li key={campus.id} className={styles.treeCampus}>
            <div className={styles.rowMain}>
              <p className={styles.rowTitle}>
                {campus.name} <span className={styles.codeTag}>{campus.code}</span>
              </p>
              <p className={styles.rowMetaLine}>
                {[campus.address, campus.city, campus.state, campus.countryCode].filter(Boolean).join(', ') ||
                  t('platform.structure.noAddress')}
              </p>
            </div>
            <ul className={styles.treeBranches}>
              {(byCampus.get(campus.id) ?? []).map((branch) => (
                <li key={branch.id} className={styles.treeBranch}>
                  <span className={styles.rowTitle}>
                    {branch.name} <span className={styles.codeTag}>{branch.code}</span>
                  </span>
                  {branch.isHeadOffice && <span className={styles.okTag}>{t('platform.structure.headOffice')}</span>}
                  <span className={styles.rowMetaLine}>
                    {[branch.city, branch.countryCode, branch.contactEmail].filter(Boolean).join(' · ') || '—'}
                  </span>
                </li>
              ))}
              {(byCampus.get(campus.id) ?? []).length === 0 && (
                <li className={styles.sectionEmpty}>{t('platform.structure.noBranches')}</li>
              )}
            </ul>
          </li>
        ))}
      </ul>

      {orphaned.length > 0 && (
        <section>
          <h3 className={styles.subheading}>{t('platform.structure.orphanedTitle')}</h3>
          <p className={styles.fieldHint}>{t('platform.structure.orphanedBody')}</p>
          <ul className={styles.rowList}>
            {orphaned.map((branch) => (
              <li key={branch.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <p className={styles.rowTitle}>
                    {branch.name} <span className={styles.codeTag}>{branch.code}</span>
                  </p>
                  <p className={styles.rowMetaLine}>{branch.campusId}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h3 className={styles.subheading}>{t('platform.structure.groupsTitle')}</h3>
        {structure.groups.length === 0 ? (
          <p className={styles.sectionEmpty}>{t('platform.structure.noGroups')}</p>
        ) : (
          <ul className={styles.rowList}>
            {structure.groups.map((membership) => (
              <li key={membership.group.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <p className={styles.rowTitle}>
                    {membership.group.name} <span className={styles.codeTag}>{membership.group.code}</span>
                  </p>
                  <p className={styles.rowMetaLine}>
                    {t(`platform.structure.groupType.${membership.group.type}`, {
                      defaultValue: membership.group.type,
                    })}
                  </p>
                </div>
                <div className={styles.rowAside}>
                  <span className={membership.role === 'HEAD_OFFICE' ? styles.okTag : styles.mutedTag}>
                    {t(`platform.structure.membership.${membership.role}`, { defaultValue: membership.role })}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className={styles.fieldHint}>
          {t('platform.structure.membershipRoles', { roles: GROUP_MEMBERSHIP_ROLES.join(', ') })}
        </p>
      </section>
    </>
  );
}

/* ─── Add campus ─── */

function AddCampusDialog({
  open,
  institutionId,
  onClose,
  onAdded,
}: {
  open: boolean;
  institutionId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateCampusRequest>({ code: '', name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ code: '', name: '', address: '', city: '', state: '', countryCode: '' });
      setError(null);
    }
  }, [open]);

  const codeValid = STRUCTURE_CODE_PATTERN.test(form.code);
  const valid = codeValid && form.name.trim().length >= 2;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.structure.addCampus(institutionId, {
        code: form.code.trim(),
        name: form.name.trim(),
        address: form.address?.trim() || undefined,
        city: form.city?.trim() || undefined,
        state: form.state?.trim() || undefined,
        countryCode: form.countryCode?.trim().toUpperCase() || undefined,
      });
      onAdded();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('platform.structure.addCampusTitle')}
      description={t('platform.structure.addCampusLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.structure.fieldCode')}</span>
            <input
              className={styles.input}
              value={form.code}
              required
              autoComplete="off"
              aria-invalid={form.code.length > 0 && !codeValid}
              onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })}
            />
            <span className={styles.fieldHint}>{t('platform.structure.fieldCodeHint')}</span>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.structure.fieldName')}</span>
            <input
              className={styles.input}
              value={form.name}
              required
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.structure.fieldAddress')}</span>
          <input
            className={styles.input}
            value={form.address ?? ''}
            onChange={(event) => setForm({ ...form, address: event.target.value })}
          />
        </label>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.structure.fieldCity')}</span>
            <input
              className={styles.input}
              value={form.city ?? ''}
              onChange={(event) => setForm({ ...form, city: event.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.structure.fieldCountry')}</span>
            <input
              className={styles.input}
              value={form.countryCode ?? ''}
              maxLength={2}
              onChange={(event) => setForm({ ...form, countryCode: event.target.value.toUpperCase() })}
            />
            <span className={styles.fieldHint}>{t('platform.structure.fieldCountryHint')}</span>
          </label>
        </div>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('platform.structure.addCampus')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ─── Add branch ─── */

function AddBranchDialog({
  open,
  institutionId,
  campuses,
  existingHeadOffice,
  onClose,
  onAdded,
}: {
  open: boolean;
  institutionId: string;
  campuses: Campus[];
  existingHeadOffice: Branch | undefined;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateBranchRequest>(emptyBranch(campuses));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(emptyBranch(campuses));
      setError(null);
    }
  }, [open, campuses]);

  const codeValid = STRUCTURE_CODE_PATTERN.test(form.code);
  const valid = codeValid && form.name.trim().length >= 2 && form.campusId.length > 0;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.structure.addBranch(institutionId, {
        campusId: form.campusId,
        code: form.code.trim(),
        name: form.name.trim(),
        isHeadOffice: form.isHeadOffice,
        address: form.address?.trim() || undefined,
        city: form.city?.trim() || undefined,
        countryCode: form.countryCode?.trim().toUpperCase() || undefined,
        contactEmail: form.contactEmail?.trim() || undefined,
        contactPhone: form.contactPhone?.trim() || undefined,
      });
      onAdded();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('platform.structure.addBranchTitle')}
      description={t('platform.structure.addBranchLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.structure.fieldCampus')}</span>
          <Select
            searchable
            value={form.campusId}
            required
            onChange={(value) => setForm({ ...form, campusId: value })}
            options={campuses.map((campus) => ({
              value: campus.id,
              label: `${campus.name} (${campus.code})`,
            }))}
          />
        </label>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.structure.fieldCode')}</span>
            <input
              className={styles.input}
              value={form.code}
              required
              autoComplete="off"
              aria-invalid={form.code.length > 0 && !codeValid}
              onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })}
            />
            <span className={styles.fieldHint}>{t('platform.structure.fieldCodeUniqueHint')}</span>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.structure.fieldName')}</span>
            <input
              className={styles.input}
              value={form.name}
              required
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </label>
        </div>

        <Checkbox
          label={t('platform.structure.headOfficeLabel')}
          checked={form.isHeadOffice ?? false}
          onChange={(event) => setForm({ ...form, isHeadOffice: event.target.checked })}
        />

        {form.isHeadOffice && existingHeadOffice && (
          <div className={styles.warningNotice} role="alert">
            <Icon name="warning" size={16} aria-hidden="true" />
            <div>
              <p className={styles.warningTitle}>{t('platform.structure.headOfficeMoveTitle')}</p>
              <p className={styles.warningBody}>
                {t('platform.structure.headOfficeMoveBody', {
                  name: existingHeadOffice.name,
                  code: existingHeadOffice.code,
                })}
              </p>
            </div>
          </div>
        )}

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.structure.fieldContactEmail')}</span>
            <input
              className={styles.input}
              type="email"
              value={form.contactEmail ?? ''}
              onChange={(event) => setForm({ ...form, contactEmail: event.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.structure.fieldContactPhone')}</span>
            <input
              className={styles.input}
              value={form.contactPhone ?? ''}
              onChange={(event) => setForm({ ...form, contactPhone: event.target.value })}
            />
          </label>
        </div>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('platform.structure.addBranch')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function emptyBranch(campuses: Campus[]): CreateBranchRequest {
  return {
    campusId: campuses[0]?.id ?? '',
    code: '',
    name: '',
    isHeadOffice: false,
    address: '',
    city: '',
    countryCode: '',
    contactEmail: '',
    contactPhone: '',
  };
}
